import React, {useCallback, useEffect, useRef, useState} from 'react';
import {TRYON_SERVER_URL} from "../config";
import {setUserData, setUserID} from "../store/AuthActions";
import {useDispatch, useSelector} from "react-redux";
import {selectAccessToken, selectAuthenticated} from "../selectors";
import {useNavigate} from "react-router-dom";
import {useAuthentication} from "../Authentication";
import ToastErrorMessage from "../components/ToastErrorMessage";
import ToastSuccessMessage from "../components/ToastSuccessMessage";
import SignInPrompt from "../components/SignInPrompt";
import {AtSignIcon, CameraIcon, ChevronRightIcon, KeyRoundIcon, MailIcon, PencilLineIcon, ShieldIcon, UserIcon} from "lucide-react";

// Placeholder for a profile value while the user details are loading.
const ValueSkeleton = ({width}) => (
    <div role="status" aria-label="Loading" className={`h-6 ${width} max-w-full animate-pulse rounded-md bg-[var(--skeleton)]`}/>
);

export default function Profile(props) {
    const accessToken = useSelector(selectAccessToken)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [username, setUsername] = useState("")
    const [userFullname, setUserFullname] = useState("")
    const [userEmail, setUserEmail] = useState("")
    const [userDetailsFetched, setUserDetailsFetched] = useState(false)
    const authenticated = useSelector(selectAuthenticated)
    const {isAuthenticated} = useAuthentication();
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [newUsername, setNewUsername] = useState(username);
    const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorStatus, setErrorStatus] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [currentFirstName, setCurrentFirstName] = useState("");
    const [currentLastName, setCurrentLastName] = useState("");
    const [showToastSuccessMessage, setShowToastSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [profileImage, setProfileImage] = useState("");
    const [avatarFailed, setAvatarFailed] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [isSendingReset, setIsSendingReset] = useState(false);
    const fileInputRef = useRef(null);

    const handleErrorClose = () => {
        setShowToastErrorMessage(false);
        setErrorMessage("");
        setErrorStatus("");
    }

    const handleSuccessClose = () => {
        setShowToastSuccessMessage(false);
        setSuccessMessage("");
    }

    async function fetchUserDetails() {
        hideToastMessages();

        try {
            console.log("access in fetch data", accessToken)
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/get/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                const userid = data.id
                dispatch(setUserID(userid));
                const dataWithoutPassword = {...data, password: undefined}; // Omit the password field
                dispatch(setUserData(JSON.stringify(dataWithoutPassword)));
                setUsername(dataWithoutPassword.username)
                setUserFullname(`${dataWithoutPassword.first_name !== undefined ? dataWithoutPassword.first_name : ""} ${dataWithoutPassword.last_name !== undefined ? dataWithoutPassword.last_name : ""}`)
                setUserEmail(dataWithoutPassword.email)
                setUserDetailsFetched(true)
                setNewUsername(dataWithoutPassword.username)
                setFirstName(dataWithoutPassword.first_name || "");
                setLastName(dataWithoutPassword.last_name || "");
                setCurrentFirstName(dataWithoutPassword.first_name || "");
                setCurrentLastName(dataWithoutPassword.last_name || "");
                const rawProfile = dataWithoutPassword.profile_url || dataWithoutPassword.profile || "";
                setProfileImage(rawProfile);
                setAvatarFailed(false);
            } else {
                console.error('Failed to fetch user data');
                setUserDetailsFetched(true)
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setUserDetailsFetched(true)
        }
    }

    const checkAuthentication = useCallback(async () => {
        try {
            const userAuthenticated = await isAuthenticated();
            if (!userAuthenticated) {
                navigate("/signin");
            }
        } catch (error) {
            console.error('Error checking authentication:', error);

            if (error.message && error.message.toLowerCase().includes('network')) {
                console.error('Network error, redirecting to login page');
                navigate("/signin");
            }
        }
    }, [dispatch, isAuthenticated, navigate]);

    useEffect(() => {
        if (authenticated && !userDetailsFetched) {
            fetchUserDetails().then(() => {
            })
        }
    }, [authenticated, userDetailsFetched]);

    const handleUpdateProfile = async (field, value) => {
        hideToastMessages();

        try {
            if (field === 'name') {
                setIsUpdatingName(true);
            } else if (field === 'username') {
                setIsUpdatingUsername(true);
            }
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/update/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(
                    field === 'name' 
                        ? { first_name: value.first_name, last_name: value.last_name }
                        : { [field]: value }
                )
            });

            if (response.ok) {
                if (field === 'name') {
                    setFirstName(value.first_name);
                    setLastName(value.last_name);
                    setUserFullname(`${value.first_name} ${value.last_name}`);
                    setIsEditingName(false);
                    setShowToastSuccessMessage(true);
                    setSuccessMessage("Profile updated successfully");
                    setIsUpdatingName(false);
                } else if (field === 'username') {
                    setUsername(value);
                    setIsEditingUsername(false);
                    setShowToastSuccessMessage(true);
                    setSuccessMessage("Profile updated successfully");
                    setIsUpdatingUsername(false);
                }
            } else {
                const errorData = await response.json();
                console.error('Failed to update profile');  
                setShowToastErrorMessage(true);
                setErrorMessage(errorData.message);
                setErrorStatus("Error");
                if (field === 'name') {
                    setIsUpdatingName(false);
                } else if (field === 'username') {
                    setIsUpdatingUsername(false);
                }
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setShowToastErrorMessage(true);
            setErrorMessage("Failed to update profile");
            setErrorStatus("Error");
            if (field === 'name') {
                setIsUpdatingName(false);
            } else if (field === 'username') {
                setIsUpdatingUsername(false);
            }
        }
    };

    const hideToastMessages = () => {
        setShowToastErrorMessage(false);
        setErrorMessage("");
        setErrorStatus("");
        setShowToastSuccessMessage(false);
        setSuccessMessage("");
    }

    const profileSrc = (() => {
        const raw = String(profileImage || "").trim();
        if (!raw) return "";
        if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("blob:") || raw.startsWith("data:")) {
            return raw;
        }
        const base = TRYON_SERVER_URL.replace(/\/$/, "");
        return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
    })();

    const displayName = userFullname.trim() || username || "Not set";
    const initials = displayName
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "U";

    const handleProfilePhoto = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        hideToastMessages();
        setIsUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append("profile", file);
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/update/`, {
                method: "POST",
                headers: { Authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to upload profile image");
            }
            setUserDetailsFetched(false);
            setShowToastSuccessMessage(true);
            setSuccessMessage("Profile photo updated");
        } catch (error) {
            setShowToastErrorMessage(true);
            setErrorMessage(error.message || "Failed to upload profile image");
            setErrorStatus("Error");
        } finally {
            setIsUploadingPhoto(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handlePasswordReset = async () => {
        if (!userEmail) {
            setShowToastErrorMessage(true);
            setErrorMessage("Email not found");
            setErrorStatus("Error");
            return;
        }
        setIsSendingReset(true);
        hideToastMessages();
        try {
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/password/reset/request/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: userEmail }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to send password reset link");
            }
            setResetEmailSent(true);
        } catch (error) {
            setShowToastErrorMessage(true);
            setErrorMessage(error.message || "Failed to send reset link. Please try again.");
            setErrorStatus("Error");
        } finally {
            setIsSendingReset(false);
        }
    };

    return <>{authenticated ? (
        <div className="h-[calc(100dvh-4.25rem)] w-full touch-pan-y overflow-x-hidden overflow-y-auto overscroll-y-contain bg-ground p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:p-6">
            <div className="mx-auto max-w-4xl space-y-8">
                <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)] sm:p-8">
                    <div className="flex flex-col items-center gap-6 sm:flex-row">
                        <div className="relative">
                            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-primary-700 shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] sm:h-28 sm:w-28">
                                {profileSrc && !avatarFailed ? (
                                    <img
                                        src={profileSrc}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        onError={() => setAvatarFailed(true)}
                                    />
                                ) : (
                                    <span className="font-Mulish text-3xl font-semibold text-white sm:text-4xl">{initials}</span>
                                )}
                            </div>
                            <div className="pointer-events-none absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[rgb(var(--rgb-sheet))] bg-white">
                                <div className="h-3 w-3 rounded-full bg-green-500" />
                            </div>
                            <label
                                htmlFor="profile-photo-upload"
                                className={`absolute -bottom-2 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-graphite shadow-[0_0_0_1px_var(--hairline)] hover:bg-primary-50 ${isUploadingPhoto ? "pointer-events-none opacity-50" : ""}`}
                                aria-label="Update profile image"
                            >
                                <CameraIcon className="h-4 w-4" />
                            </label>
                            <input
                                id="profile-photo-upload"
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="sr-only"
                                onChange={handleProfilePhoto}
                            />
                        </div>
                        <div className="min-w-0 text-center sm:text-left">
                            <h1 className="font-Mulish text-xl font-semibold text-secondary">
                                {userDetailsFetched ? displayName : <ValueSkeleton width="w-48" />}
                            </h1>
                            <p className="mt-1 flex items-center justify-center gap-2 font-Mulish text-sm text-graphite sm:justify-start">
                                <AtSignIcon className="h-4 w-4 shrink-0" />
                                <span className="min-w-0 break-all">{userDetailsFetched ? (username || "Not set") : <ValueSkeleton width="w-32" />}</span>
                            </p>
                            <p className="mt-2 flex items-center justify-center gap-2 font-Mulish text-sm text-graphite sm:justify-start">
                                <MailIcon className="h-3.5 w-3.5 shrink-0" />
                                <span className="min-w-0 truncate">{userDetailsFetched ? (userEmail || "No email set") : <ValueSkeleton width="w-56" />}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <div className="mb-2 flex items-center gap-2">
                            <UserIcon className="h-[18px] w-[18px] text-graphite" />
                            <h2 className="font-Mulish text-lg font-semibold text-secondary">Account Details</h2>
                        </div>
                        <div className="divide-y divide-hairline overflow-hidden rounded-xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                            <div className="p-4 transition-colors hover:bg-hover-tint sm:p-5">
                                <div className={`flex gap-3 ${isEditingName ? "flex-col sm:flex-row sm:items-center sm:justify-between" : "items-center justify-between"}`}>
                                    <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ground">
                                            <UserIcon className="h-[18px] w-[18px] text-graphite" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-Mulish text-sm text-graphite">Full name</p>
                                            {!isEditingName ? (
                                                <p className="mt-0.5 font-Mulish text-sm font-semibold text-secondary">{userDetailsFetched ? displayName : <ValueSkeleton width="w-40" />}</p>
                                            ) : (
                                                <div className="mt-2 grid grid-cols-2 gap-2">
                                                    <input
                                                        className="min-w-0 w-full rounded-lg bg-[var(--field-bg)] px-3 py-1.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                        value={currentFirstName}
                                                        onChange={(e) => setCurrentFirstName(e.target.value)}
                                                        placeholder="First"
                                                    />
                                                    <input
                                                        className="min-w-0 w-full rounded-lg bg-[var(--field-bg)] px-3 py-1.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                        value={currentLastName}
                                                        onChange={(e) => setCurrentLastName(e.target.value)}
                                                        placeholder="Last"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!isEditingName ? (
                                        <button
                                            type="button"
                                            className="rounded-lg p-2 text-graphite hover:bg-hover-tint"
                                            aria-label="Edit full name"
                                            onClick={() => setIsEditingName(true)}
                                        >
                                            <PencilLineIcon className="h-[18px] w-[18px]" />
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2 sm:shrink-0">
                                            <button
                                                type="button"
                                                disabled={isUpdatingName}
                                                onClick={() => handleUpdateProfile("name", { first_name: currentFirstName, last_name: currentLastName })}
                                                className="flex-1 rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white disabled:opacity-40 sm:flex-none"
                                            >
                                                {isUpdatingName ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={isUpdatingName}
                                                onClick={() => {
                                                    setIsEditingName(false);
                                                    setCurrentFirstName(firstName);
                                                    setCurrentLastName(lastName);
                                                }}
                                                className="flex-1 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50 sm:flex-none"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 transition-colors hover:bg-hover-tint sm:p-5">
                                <div className={`flex gap-3 ${isEditingUsername ? "flex-col sm:flex-row sm:items-center sm:justify-between" : "items-center justify-between"}`}>
                                    <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ground">
                                            <AtSignIcon className="h-[18px] w-[18px] text-graphite" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-Mulish text-sm text-graphite">Username</p>
                                            {!isEditingUsername ? (
                                                <p className="mt-0.5 font-Mulish text-sm font-semibold text-secondary">{userDetailsFetched ? (username || "Not set") : <ValueSkeleton width="w-32" />}</p>
                                            ) : (
                                                <input
                                                    className="mt-2 w-full min-w-0 rounded-lg bg-[var(--field-bg)] px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                    value={newUsername}
                                                    onChange={(e) => setNewUsername(e.target.value)}
                                                    placeholder="Username"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {!isEditingUsername ? (
                                        <button
                                            type="button"
                                            className="rounded-lg p-2 text-graphite hover:bg-hover-tint"
                                            aria-label="Edit username"
                                            onClick={() => setIsEditingUsername(true)}
                                        >
                                            <PencilLineIcon className="h-[18px] w-[18px]" />
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2 sm:shrink-0">
                                            <button
                                                type="button"
                                                disabled={isUpdatingUsername}
                                                onClick={() => handleUpdateProfile("username", newUsername)}
                                                className="flex-1 rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white disabled:opacity-40 sm:flex-none"
                                            >
                                                {isUpdatingUsername ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={isUpdatingUsername}
                                                onClick={() => {
                                                    setIsEditingUsername(false);
                                                    setNewUsername(username);
                                                }}
                                                className="flex-1 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50 sm:flex-none"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 transition-colors hover:bg-hover-tint sm:p-5">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex min-w-0 items-center gap-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ground">
                                            <MailIcon className="h-[18px] w-[18px] text-graphite" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-Mulish text-sm text-graphite">Email address</p>
                                            <p className="mt-0.5 truncate font-Mulish text-sm font-semibold text-secondary">{userDetailsFetched ? (userEmail || "Not set") : <ValueSkeleton width="w-56" />}</p>
                                        </div>
                                    </div>
                                    <span className="inline-flex shrink-0 rounded-full bg-primary-50 px-2.5 py-1 font-Mulish text-xs text-primary-700 sm:px-3 sm:text-sm">
                                        Verified
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="mb-2 flex items-center gap-2">
                            <ShieldIcon className="h-[18px] w-[18px] text-graphite" />
                            <h2 className="font-Mulish text-lg font-semibold text-secondary">Security</h2>
                        </div>
                        <div className="overflow-hidden rounded-xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                            <button
                                type="button"
                                onClick={() => {
                                    setResetEmailSent(false);
                                    setShowResetModal(true);
                                }}
                                className="group w-full p-4 text-left transition-colors hover:bg-hover-tint sm:p-5"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-ground">
                                            <KeyRoundIcon className="h-[18px] w-[18px] text-graphite" />
                                        </div>
                                        <div>
                                            <p className="font-Mulish text-sm font-semibold text-secondary">Password</p>
                                            <p className="mt-0.5 font-Mulish text-sm text-graphite">Reset via email</p>
                                        </div>
                                    </div>
                                    <ChevronRightIcon className="h-[18px] w-[18px] text-graphite transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </button>
                        </div>
                        <div className="rounded-xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] sm:p-5">
                            <p className="mb-3 font-Mulish text-sm text-graphite">Account status</p>
                            <div className="flex items-center justify-between">
                                <span className="font-Mulish text-sm text-graphite">Status</span>
                                <span className="inline-flex items-center gap-1.5 font-Mulish text-sm font-semibold text-primary-700">
                                    <span className="h-2 w-2 rounded-full bg-primary-700" />
                                    Active
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showResetModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
                    onClick={() => {
                        if (!isSendingReset) {
                            setShowResetModal(false);
                            setResetEmailSent(false);
                        }
                    }}
                >
                    <div
                        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {!resetEmailSent ? (
                            <>
                                <h2 className="font-Mulish text-lg font-semibold text-secondary">Reset password</h2>
                                <p className="mt-3 font-Mulish text-sm text-graphite">
                                    We'll send a password reset link to <span className="font-semibold text-secondary">{userEmail}</span>
                                </p>
                                <div className="mt-5 flex gap-3">
                                    <button
                                        type="button"
                                        disabled={isSendingReset}
                                        onClick={() => setShowResetModal(false)}
                                        className="flex-1 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSendingReset}
                                        onClick={handlePasswordReset}
                                        className="flex-1 rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white disabled:opacity-40"
                                    >
                                        {isSendingReset ? "Sending..." : "Send Reset Link"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="py-2 text-center">
                                <h2 className="font-Mulish text-lg font-semibold text-secondary">Check your email</h2>
                                <p className="mt-2 font-Mulish text-sm text-graphite">
                                    We've sent a password reset link to <span className="font-semibold text-secondary">{userEmail}</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowResetModal(false);
                                        setResetEmailSent(false);
                                    }}
                                    className="mt-5 w-full rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showToastErrorMessage && (
                <ToastErrorMessage errorMessage={errorMessage} errorStatus={errorStatus} handleErrorClose={handleErrorClose} />
            )}

            {showToastSuccessMessage && (
                <ToastSuccessMessage message={successMessage} handleClose={handleSuccessClose} />
            )}
        </div>) : authenticated === false ? (
            <div className="flex h-[calc(100dvh-4.25rem)] items-center justify-center overflow-y-auto bg-ground p-4">
                <div className="w-full max-w-md">
                    <SignInPrompt
                        title="Sign in to see your profile"
                        message="Your account details will show up here."
                        onSignIn={() => navigate("/signin")}
                    />
                </div>
            </div>
        ) : (
            <div className="flex h-[calc(100dvh-4.25rem)] items-center justify-center bg-ground">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-100 border-t-primary-700"></div>
            </div>
        )}
    </>
}